import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Check, X, Edit3, ChevronDown, ChevronUp } from 'lucide-react';
import SolutionField from './SolutionField';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SolutionDrawer — expandable panel below an action showing the AI-generated
 * ready-to-paste solution. Supports Generate / Approve / Reject / Edit flow.
 *
 * Phase-2 core: solutions are NEVER auto-executed. Approval only marks the
 * solution as approved — actual push-to-production comes in Phase-3.
 */
export default function SolutionDrawer({ action, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [solution, setSolution] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/growth-os/actions/${action.action_id}/solution`,
        { withCredentials: true }
      );
      if (data?.solution) {
        setSolution(data.solution);
        // Merge edited_content over original content so partial edits don't erase other fields
        setDraft({ ...(data.solution.content || {}), ...(data.solution.edited_content || {}) });
      }
    } catch (_) { /* noop */ }
    setLoading(false);
  };

  const generate = async (force = false) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/growth-os/actions/${action.action_id}/generate-solution?force=${force}`,
        {},
        { withCredentials: true, timeout: 90000 }
      );
      if (data?.solution) {
        setSolution(data.solution);
        setDraft({ ...(data.solution.content || {}), ...(data.solution.edited_content || {}) });
        toast.success(data.cached ? 'Solution loaded from cache' : '✨ AI solution generated');
      }
    } catch (err) {
      toast.error('Generation failed: ' + (err?.response?.data?.detail || err?.message));
    }
    setLoading(false);
  };

  const approve = async () => {
    const wasEdited = editMode && draft && JSON.stringify(draft) !== JSON.stringify(solution.content);
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/growth-os/actions/${action.action_id}/approve`,
        wasEdited ? { edited_content: draft, note: 'Approved via dashboard' } : { note: 'Approved via dashboard' },
        { withCredentials: true }
      );
      toast.success('✅ Solution approved (not yet auto-executed — Phase 3)');
      setEditMode(false);
      onStatusChange && onStatusChange();
    } catch (err) {
      toast.error('Approve failed: ' + (err?.response?.data?.detail || err?.message));
    }
    setLoading(false);
  };

  const reject = async () => {
    const reason = window.prompt('Reject reason (optional):') || '';
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/growth-os/actions/${action.action_id}/reject`,
        { reason },
        { withCredentials: true }
      );
      toast.info('❌ Rejected');
      onStatusChange && onStatusChange();
    } catch (err) {
      toast.error('Reject failed: ' + (err?.response?.data?.detail || err?.message));
    }
    setLoading(false);
  };

  const copyToClipboard = (v) => {
    const text = typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v);
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !solution) load();
  };

  const c = draft || solution?.content || {};

  const handleFieldChange = (path, value) => setDraft({ ...draft, [path]: value });

  const isReady = solution && solution.status === 'ready';
  const isEdited = solution && solution.status === 'edited';
  const isApproved = solution && solution.status === 'approved';
  const isRejected = solution && solution.status === 'rejected';

  return (
    <div className="border-t border-white/5" data-testid={`solution-drawer-${action.action_id}`}>
      <button
        onClick={toggleOpen}
        className="w-full px-4 py-2 text-xs text-gold hover:bg-white/5 flex items-center justify-between transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          {solution ? `Solution ${solution.status}` : 'AI Solution'}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="p-4 bg-black/20 border-t border-white/5">
          {loading && (
            <div className="text-xs text-gold animate-pulse">⏳ Loading solution…</div>
          )}

          {!loading && !solution && (
            <div className="text-center py-4">
              <p className="text-xs text-text-muted mb-3">Koi AI solution abhi tak generate nahi hua.</p>
              <button
                onClick={() => generate(false)}
                data-testid={`generate-solution-${action.action_id}`}
                className="px-4 py-1.5 rounded bg-gold text-[#0a0820] text-xs font-semibold hover:brightness-110"
              >
                ✨ Generate AI Solution
              </button>
            </div>
          )}

          {!loading && solution && (
            <>
              {/* Header + confidence + status */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="text-xs">
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${
                    isApproved ? 'bg-emerald-500/20 text-emerald-300' :
                    isRejected ? 'bg-red-500/20 text-red-300' :
                    isEdited ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-gold/20 text-gold'
                  }`}>{solution.status.toUpperCase()}</span>
                  <span className="ml-2 text-text-muted">
                    Confidence: <strong className="text-text-main">{c.confidence}</strong>
                  </span>
                </div>
                <div className="flex gap-2">
                  {(isReady || isEdited) && (
                    <>
                      <button
                        onClick={() => { setEditMode(!editMode); setDraft({ ...c }); }}
                        data-testid={`edit-solution-${action.action_id}`}
                        className="px-3 py-1 rounded border border-gold/50 text-gold text-xs font-semibold hover:bg-gold/10"
                      >
                        <Edit3 className="w-3 h-3 inline mr-1" />
                        {editMode ? 'Cancel Edit' : 'Edit'}
                      </button>
                      <button
                        onClick={approve}
                        data-testid={`approve-solution-${action.action_id}`}
                        className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold"
                      >
                        <Check className="w-3 h-3 inline mr-1" /> Approve
                      </button>
                      <button
                        onClick={reject}
                        data-testid={`reject-solution-${action.action_id}`}
                        className="px-3 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-semibold"
                      >
                        <X className="w-3 h-3 inline mr-1" /> Reject
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => generate(true)}
                    className="px-3 py-1 rounded border border-gold/30 text-gold/80 text-xs hover:bg-gold/10"
                  >
                    🔄 Regenerate
                  </button>
                </div>
              </div>

              {/* Expected impact */}
              {c.expected_impact && (
                <div className="mb-4 px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200">
                  💡 <strong>Expected Impact:</strong> {c.expected_impact}
                </div>
              )}

              {/* Fields */}
              <SolutionField label="SEO Title" path="seo_title" value={c.seo_title} editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="Meta Description" path="meta_description" value={c.meta_description} multiline editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="H1 (Hindi)" path="h1_hindi" value={c.h1_hindi} hindi editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="H1 (English)" path="h1_english" value={c.h1_english} editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="Canonical URL" path="canonical_url" value={c.canonical_url} editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="Structured Data (JSON-LD)" path="structured_data_jsonld" value={c.structured_data_jsonld} multiline="json" editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="Alt Text Templates" path="alt_text_templates" value={c.alt_text_templates} multiline="json" editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="Internal Links" path="internal_links" value={c.internal_links} multiline="json" editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="Content Suggestions" path="content_suggestions" value={c.content_suggestions} multiline="json" editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />
              <SolutionField label="Technical Fix Code" path="technical_fix_code" value={c.technical_fix_code} multiline editMode={editMode} onChange={handleFieldChange} onCopy={copyToClipboard} />

              {/* Approval trail */}
              {(isApproved || isRejected) && (
                <div className="mt-4 pt-3 border-t border-white/5 text-xs text-text-muted">
                  {isApproved && <>✅ Approved by {solution.approved_by} at {solution.approved_at}</>}
                  {isRejected && <>❌ Rejected: {solution.rejection_reason || 'no reason'}</>}
                  <div className="mt-1 text-[10px] italic">
                    Note: Approval only records intent. Auto-execute engine (Phase-3) will push to production only after Guruji&apos;s tap.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
